import { useState, useRef, useCallback } from 'react';

/**
 * Hook for managing chat-input attachments.
 *
 * `attachedFiles` holds rich descriptors (not raw File objects):
 *   {
 *     tempId: string,       // stable client-side id for add/update/remove
 *     id?: string,          // backend document_id once upload succeeds
 *     filename: string,
 *     mime_type: string,
 *     file_size: number,
 *     is_image: boolean,
 *     uploading?: boolean,  // true while the upload is in flight
 *     upload_error?: string // set on failure
 *   }
 *
 * The drop handler delegates to `onFilesDropped(files)` (provided by the
 * page) which is responsible for uploading and calling `addAttachedFile` /
 * `updateAttachedFile` to surface pills in the input. The hook itself does
 * not upload — that lives in page-level code so per-page policy (folder
 * placement, allowed types) stays out of the hook.
 */

const isImageMime = (mime) => typeof mime === 'string' && mime.startsWith('image/');

export const makeAttachmentDescriptor = (file, overrides = {}) => ({
    tempId: `att-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    filename: file?.name,
    mime_type: file?.type || 'application/octet-stream',
    file_size: file?.size || 0,
    is_image: isImageMime(file?.type),
    uploading: true,
    ...overrides,
});

export const useFileAttachment = (onFilesDropped = null) => {
    const [attachedFiles, setAttachedFiles] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const dragCounter = useRef(0);

    const hasFiles = (dataTransfer) => {
        if (!dataTransfer?.items) return false;
        for (const item of dataTransfer.items) {
            if (item.kind === 'file') return true;
        }
        return false;
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current++;
        if (hasFiles(e.dataTransfer)) {
            setIsDragging(true);
        }
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current--;
        if (dragCounter.current === 0) {
            setIsDragging(false);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        dragCounter.current = 0;

        const files = [...e.dataTransfer.files];
        if (files && files.length > 0 && onFilesDropped) {
            onFilesDropped(files);
        }
    };

    const addAttachedFile = useCallback((descriptor) => {
        setAttachedFiles((prev) => [...prev, descriptor]);
    }, []);

    const updateAttachedFile = useCallback((tempId, patch) => {
        setAttachedFiles((prev) =>
            prev.map((f) => (f.tempId === tempId ? { ...f, ...patch } : f)),
        );
    }, []);

    const removeAttachment = useCallback((descriptor) => {
        setAttachedFiles((prev) =>
            prev.filter((f) => f.tempId !== descriptor.tempId && f !== descriptor),
        );
    }, []);

    const clearAttachments = useCallback(() => {
        setAttachedFiles([]);
    }, []);

    return {
        attachedFiles,
        isDragging,
        handleDragEnter,
        handleDragLeave,
        handleDragOver,
        handleDrop,
        addAttachedFile,
        updateAttachedFile,
        removeAttachment,
        clearAttachments,
        setAttachedFiles,
    };
};
